import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext } from "react";
import { useParams } from "react-router";
import { ShopContext } from "utils/Shop";

import { useGetScreenByIdQuery } from "../api/react-query/queries/screens";
import { EMPTY_PRICES_CROSSED } from "../Constants";
import { getSortingForPrice, isPortrait } from "../utils/PricelistUtils";
import { JsonPrice, Price } from "../utils/types";
import { formatMoney } from "../utils/Utils";
import { Badge } from "./Badge";
import {
    easyGoingBadges,
    easyGoingBadgesVertical,
    maryJaneBadges,
    maryJaneBadgesVertical,
    mississippiBadges,
    mississippiBadgesVertical,
} from "./PriceRowStyling";
import ProductImage from "./ProductImage";

function renderIcons(icons: string[]) {
    const arr = icons.flatMap((item) => Array.from(item));
    return arr.map((icon, idx) => {
        const prev = arr[idx - 1];
        const isDuplicate = prev === icon;

        return (
            <span key={idx} className={`extraIcon ${isDuplicate ? "stackedIcon" : ""}`}>
                {icon}
            </span>
        );
    });
}

const ProductWithIcons = ({ price }: { price: Price }) => {
    const text = !price.kind_id ? price.product_name : price.kind_name;
    const words = text.split(" ");
    const lastWord = words.pop(); // Get the last word
    const firstPart = words.join(" "); // Reconstruct the rest of the sentence

    return (
        <span>
            {firstPart} {firstPart && " "}
            <span style={{ whiteSpace: "nowrap" }}>
                {lastWord}
                {price.category_screen_display_options &&
                    price.category_screen_display_options.show_flags &&
                    price.flags &&
                    price.flags.map((flag: string) => <span className="flag">{flag}</span>)}
                {price.new && (
                    <FontAwesomeIcon
                        className="newBadge"
                        style={{
                            marginTop: "auto",
                            marginBottom: "auto",
                            fontSize: "1.1vw",
                            marginLeft: "0.5vw",
                        }}
                        icon={faStar}
                        color={"#d1cb28"}
                    />
                )}
                {price.icons && renderIcons(price.icons)}
            </span>
        </span>
    );
};

const PriceBlock = ({
    jsonPrice,
    col,
    discountPrice,
}: {
    jsonPrice: JsonPrice;
    col: string | number;
    discountPrice?: number;
}) => {
    const { screenId } = useParams();
    const { screen } = useGetScreenByIdQuery(screenId, false);
    const isUsingTruncatedPrices = screen?.extra_config?.use_truncated_prices ?? false;

    const showCross = jsonPrice.label !== col;
    return (
        <div className="priceContainer">
            {discountPrice && (
                <p className="oldPrice">
                    {showCross
                        ? EMPTY_PRICES_CROSSED
                            ? "x"
                            : ""
                        : formatMoney(jsonPrice.price, isUsingTruncatedPrices ? "short" : "long")}
                </p>
            )}
            <p className={discountPrice ? "discountedPrice" : "price"}>
                {showCross
                    ? EMPTY_PRICES_CROSSED
                        ? "x"
                        : ""
                    : formatMoney(discountPrice ?? jsonPrice.price, isUsingTruncatedPrices ? "short" : "long")}
            </p>
        </div>
    );
};

function shadeColor(hex: string, percent: number, opacity: number) {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, "");

    // Parse r, g, b
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Calculate new r, g, b
    r = Math.min(255, Math.max(0, r + r * percent));
    g = Math.min(255, Math.max(0, g + g * percent));
    b = Math.min(255, Math.max(0, b + b * percent));

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const calculateRowColor = (index: number, baseColor: string, opacity: number) => {
    return index % 2 === 0
        ? shadeColor(baseColor, -0.25, opacity) // 10% darker
        : shadeColor(baseColor, 0.25, opacity); // 10% lighter
};

// Helper to render prices for a column
const ColumnPrices = ({ price, col }: { price: Price; col: string | number }) => {
    const { json_prices, action_labels } = price;
    const { config } = useContext(ShopContext);

    if (json_prices) {
        const discountMap = new Map<string, number>((action_labels || []).map((al) => [al.label, al.price]));

        return (
            <div>
                {json_prices.map((jsonPrice, index) => {
                    const discountPrice = discountMap.get(jsonPrice.label);
                    return <PriceBlock key={index} jsonPrice={jsonPrice} discountPrice={discountPrice} col={col} />;
                })}
            </div>
        );
    }
    const piecePrice =
        price.piece && price.piece > 0 ? formatMoney(price.piece, config.use_truncated_prices ? "short" : "long") : "";
    return <p className="price">{piecePrice}</p>;
};

// Main component
const PriceGrid = ({ cols, price }: { cols: (string | number)[]; price: Price }) => {
    return (
        <div style={{ justifyContent: "flex-end", display: "flex", alignItems: "center", marginLeft: "-1vw" }}>
            {cols.map((col, index) => (
                <div key={index}>
                    <ColumnPrices price={price} col={col} />
                </div>
            ))}
        </div>
    );
};

export const PriceRowNew = (props) => {
    const { prices, cols, shopId } = props;
    const { screenId } = useParams();

    const styles = (() => {
        switch (shopId) {
            case "bbc0cfa7-e230-4338-817a-55190debdac0":
                return isPortrait ? maryJaneBadgesVertical : maryJaneBadges;
            case "ddd70853-5e9d-45ff-82b3-24c9d81d4e32":
                return isPortrait ? easyGoingBadgesVertical : easyGoingBadges;
            default:
                return isPortrait ? mississippiBadgesVertical : mississippiBadges;
        }
    })();

    return (
        <div>
            {prices
                .sort((p1: Price, p2: Price) => {
                    if (p1.json_prices && p2.json_prices) {
                        return getSortingForPrice(
                            p1,
                            p2,
                            p1.category_screen_display_options?.include_joints_in_sorting,
                            p1.category_screen_display_options?.sort_prices_descending
                        );
                    } else {
                        return p1.piece - p2.piece;
                    }
                })
                .map((price, index) => {
                    let description = price.kind_short_description_nl;
                    if (
                        price.strains.length &&
                        price.category_screen_display_options &&
                        price.category_screen_display_options.show_strains
                    ) {
                        description = price.strains.map((strain, index) => (
                            <span key={index}>
                                {console.log("strain", strain)}
                                {strain.name}
                                {index !== price.strains.length - 1 ? " - " : ""}
                            </span>
                        ));
                        console.log("DESCRIPTION", description);
                    }

                    const showChis = price.category_screen_display_options?.show_chis;
                    const showKindBadge = showChis || !screenId;

                    return (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor:
                                    price.category_screen_display_options &&
                                    price.category_screen_display_options.use_custom_color
                                        ? calculateRowColor(
                                              index,
                                              price.category_color,
                                              price.category_screen_display_options.color_opacity ?? 0.35
                                          )
                                        : "",
                            }}
                            key={index}
                            className="row"
                        >
                            <div style={{ display: "flex" }}>
                                <div className="rowContent">
                                    {showKindBadge && (
                                        <div className="kindBadge">
                                            {price.kind_i ? (
                                                <Badge styles={styles} letter={"i"} />
                                            ) : price.kind_c ? (
                                                <Badge styles={styles} letter={"c"} />
                                            ) : price.kind_s ? (
                                                <Badge styles={styles} letter={"s"} />
                                            ) : price.kind_h ? (
                                                <Badge styles={styles} letter={"h"} />
                                            ) : (
                                                <Badge styles={styles} letter={"h"} />
                                            )}
                                        </div>
                                    )}
                                    {price.category_screen_display_options &&
                                        price.category_screen_display_options.show_images &&
                                        price.image_1 && (
                                            <div className="kindBadge">
                                                <ProductImage imageKey={price.image_1} width={32} height={32} />
                                            </div>
                                        )}
                                    <div className="nameDescription">
                                        <div className="nameStar">
                                            <h4
                                                className="nameprice"
                                                style={{ paddingLeft: showKindBadge ? "0" : "4.5vw" }}
                                            >
                                                <ProductWithIcons price={price} />
                                            </h4>
                                        </div>
                                        {price.category_screen_display_options &&
                                            price.category_screen_display_options.show_product_description && (
                                                <p className="description">{description}</p>
                                            )}
                                    </div>
                                </div>
                            </div>
                            <div className="priceGrid">
                                <PriceGrid cols={cols} price={price} />
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};
